import { dequal } from "dequal"
import type {
  SeriesAreaOptions,
  SeriesColumnOptions,
  SeriesLineOptions,
  YAxisOptions,
} from "highcharts"
import { useRef } from "react"
import { createYAxis } from "./axes/yAxes"
import type { ChartMetricApproximation, GranularityType } from "./Chart"
import { SERIE_FORMAT, SERIE_STACK, type SERIE_TYPE } from "./ChartOptions"
import { getComputedVariableColor } from "./colors"
import { getFormatter as getTooltipFormatter } from "./tooltipFormatters"
import { getValueFormatter as getTooltipValueFormatter } from "./tooltipValueFormatters"
import { createSerieId } from "./utils/createSerieId"

/** @deprecated */
export type ChartSerieNonTimeData = Array<[string, number]>
/** @deprecated */
export type ChartSerieTimeData = Array<[number, number | null]>
/** @deprecated */
export type ChartSerieData = ChartSerieTimeData | ChartSerieNonTimeData

export interface ChartSerie {
  info?: {
    data_id?: string | undefined
    product_id?: string | undefined
    market_sector_id?: string | undefined
    chain_id?: string | undefined
    name?: string | undefined
    legendLabel?: string | undefined
  }
  metricId?: string
  id?: string
  ratioMetricId?: string
  yAxis: string
  name: string
  label: string
  seriesTitle: string
  type: "line" | "area" | "column"
  format: `${SERIE_FORMAT}`
  opposite?: boolean
  index?: number
  legendIndex?: number
  color?: string
  logarithmic?: boolean
  stack?: `${SERIE_STACK}`
  reverse?: boolean
  cumulative?: boolean
  percentage?: boolean
  tooltipOptions?: {
    token?: string
    isLowerValueBetter?: boolean
  }
  groupingApproximation?: ChartMetricApproximation
  visible?: boolean
  isStatic?: boolean
  groupByRemainingInOther?: boolean
  /** Area fill opacity for this serie. Overrides the `plotOptions.area`
   * default (0.3) on the serie itself, so every chart instance rendering
   * this serie paints the same fill — the inline card, the expand modal and
   * the copy-as-image capture. Ignored for line and column series. */
  fillOpacity?: number
  /** Point-marker options for this serie, passed straight to Highcharts.
   *
   * Highcharts otherwise cycles its default symbols by series index — circle,
   * diamond, square, triangle, triangle-down — so a chart with many series
   * shows a different shape per band on hover. Set `{ symbol: "circle" }` to
   * keep them uniform.
   *
   * Opt-in: series that don't set this keep Highcharts' defaults, so existing
   * charts are unaffected. */
  marker?: SeriesAreaOptions["marker"]
  /** Marks a serie that rides alongside the others rather than belonging to
   * them — a count drawn over a chart of dollars, on its own axis.
   *
   * The tooltip treats it as a separate reading: it is left out of the total,
   * because adding a holder count to a sum of dollars produces a number that
   * means nothing, and it is listed first rather than in value order, because
   * ranking it against the stack would sort a figure in the millions below
   * every band and the panel only shows its first ten rows.
   *
   * Opt-in: series that don't set this are ranked and totalled exactly as
   * before, so existing charts are unaffected. */
  overlay?: boolean
  /** Names this serie's axis, instead of the axis's own identity string.
   *
   * `yAxis` does double duty: it binds series to an axis AND supplies that
   * axis's title. Charts that title their axes from the chart config get the
   * title overridden per axis, but only for as many axes as there are configs
   * — an axis introduced by a serie alone falls back to its identity string,
   * which is a key, not a label. Set this to say what the axis is called, or
   * to `""` to leave it untitled.
   *
   * Opt-in: series that don't set it keep deriving the title from `yAxis`. */
  yAxisTitle?: string
  /** Stroke width for this serie, overriding the `plotOptions` default (2 for
   * line, 1.75 for area). Line and area only.
   *
   * Opt-in: series that don't set it keep the chart's default weight. */
  lineWidth?: number
}

/** @deprecated */
export const convertChartDataToAggregatedData = (data: Array<ChartSerieTimeData>) => {
  const aggregatedValues = new Map<number, number>()
  data.forEach((serie) => {
    serie.forEach(([timestamp, value]) => {
      const current = aggregatedValues.get(timestamp) ?? 0
      aggregatedValues.set(timestamp, current + (value ?? 0))
    })
  })

  return Array.from(aggregatedValues.entries()).sort((a, b) => a[0] - b[0])
}

/** @deprecated */
export function useYAxisOptions(
  series: Array<ChartSerie>,
  { isMobile = false, aggregated = false }: { isMobile: boolean; aggregated: boolean },
  yAxisOptions?: Array<YAxisOptions> | YAxisOptions,
) {
  const cachedAxes = useRef<Array<YAxisOptions>>([])
  const cache = useRef<{
    series: Array<ChartSerie>
    isMobile: boolean
    aggregated: boolean
    yAxisOptions?: Array<YAxisOptions> | YAxisOptions
  }>({
    series: [],
    isMobile: false,
    aggregated: false,
    yAxisOptions: [],
  })

  if (!dequal(cache.current, { series, isMobile, aggregated, yAxisOptions })) {
    cache.current = { series, isMobile, aggregated, yAxisOptions }

    const yAxis: Array<YAxisOptions> = []
    const seenYAxis = new Set<string>()
    let axisIndex = 0

    series.forEach((serie) => {
      const yAxisName = serie.yAxis
      const isPercentageStack = !!(serie.percentage || serie.stack === SERIE_STACK.PERCENTAGE)

      if (!seenYAxis.has(yAxisName)) {
        let opposite = axisIndex % 2 === 1 && !isMobile
        if (typeof serie.opposite !== "undefined") {
          opposite = serie.opposite
        }

        const normalizedAxisOptions =
          (Array.isArray(yAxisOptions) ? yAxisOptions[axisIndex] : yAxisOptions) ?? {}
        const axis: YAxisOptions = createYAxis(serie.yAxis, {
          label: aggregated ? "" : (serie.yAxisTitle ?? serie.yAxis),
          format: isPercentageStack ? "percent" : serie.format,
          opposite,
          isPercentageStack,
          isLogarithmic: serie.logarithmic || false,
          isReversed: serie.cumulative || serie.reverse || false,
          options: normalizedAxisOptions,
        })

        seenYAxis.add(yAxisName)
        yAxis.push(axis)
        axisIndex++
      }
    })

    cachedAxes.current = yAxis
  }

  return cachedAxes.current
}

/** @deprecated */
export function createChartSerie<T extends `${SERIE_TYPE}`>(
  serie: Omit<ChartSerie, "color"> & { color: string },
  {
    granularity,
    enableDataGrouping = false,
  }: { granularity: GranularityType; enableDataGrouping?: boolean },
): T extends "line"
  ? SeriesLineOptions
  : T extends "area"
    ? SeriesAreaOptions
    : SeriesColumnOptions {
  const isPercentageStack = (serie.percentage || serie.stack === SERIE_STACK.PERCENTAGE) ?? false

  const highChartSerie: SeriesLineOptions | SeriesAreaOptions | SeriesColumnOptions = {
    type: serie.type,
    name: serie.label,
    visible: serie.visible,
    id: createSerieId(serie.name),
    color: getComputedVariableColor(serie.color),
    threshold: 0,
    tooltip: {
      pointFormatter: getTooltipFormatter(
        isPercentageStack ? SERIE_FORMAT.PERCENTAGE : serie.format,
        {
          ...serie.tooltipOptions,
          isPercentageShare: isPercentageStack,
        },
      ),
    },
    yAxis: serie.yAxis,
    stacking: serie.stack,
    legendIndex: serie.legendIndex,
    index: serie.index,
    // Full charts use our pre-grouped granularity. Small previews can opt in to
    // one more display-only grouping pass based on the available pixel width.
    dataGrouping: enableDataGrouping
      ? {
          enabled: true,
          approximation: serie.groupingApproximation,
        }
      : { enabled: false },
    // we use this in the tooltip formatter for quarter
    custom: {
      granularity,
      isOverlay: serie.overlay,
      formatValue: getTooltipValueFormatter(
        isPercentageStack ? SERIE_FORMAT.PERCENTAGE : serie.format,
        {
          ...serie.tooltipOptions,
          isPercentageShare: isPercentageStack,
        },
      ),
    },
  }

  if (serie.type === "column" && !serie.stack) {
    highChartSerie.states = {
      hover: {
        brightness: -0.15,
      },
    }
  }

  // Serie-level fill wins over `plotOptions.area`, and it survives the
  // internal plotOptions rebuilds that a chart does on redraw.
  if (highChartSerie.type === "area" && typeof serie.fillOpacity === "number") {
    highChartSerie.fillOpacity = serie.fillOpacity
  }

  // Same rationale as fillOpacity: carried on the serie so every chart
  // instance rendering it (card, expand modal, image capture) matches.
  // Column series type their marker options differently; only line and area
  // accept the point-marker shape, which is where the symbol cycling shows.
  if (serie.marker && (highChartSerie.type === "area" || highChartSerie.type === "line")) {
    highChartSerie.marker = serie.marker
  }

  // Same opt-in rationale as the two above: a serie that wants a stroke unlike
  // the chart's default carries it, so every instance of it matches.
  if (
    typeof serie.lineWidth === "number" &&
    (highChartSerie.type === "area" || highChartSerie.type === "line")
  ) {
    highChartSerie.lineWidth = serie.lineWidth
  }

  return highChartSerie as T extends "line"
    ? SeriesLineOptions
    : T extends "area"
      ? SeriesAreaOptions
      : SeriesColumnOptions
}
